// Contribution from Mercatura DeFi Team to Menese Protocol
// (2-of-2 Threshold ECDSA — Tier 7 frontend application).
// Mercatura Forum proprietary.
//
// Plain-TS app shell — no framework. Three view modes: `loggedout`,
// `onboarding`, `signed_in`. Wires II login + onboarding +
// signMessage from `flow.ts`. Strict-CSP-compatible (no inline event
// handlers; no `eval`).

import { Ed25519KeyIdentity } from "@dfinity/identity";

import { IiAuth } from "./lib/ii";
import { MeneseCanister } from "./lib/canister/agent";
import {
  assertCombinedVersionMatches,
  clearSessionCache,
  onboardingRegister,
  prefetchPresig,
  signMessage,
} from "./lib/flow";
import { hasLocalCredentialId } from "./lib/passkey";
import { MENESE_COMBINED_VERSION } from "./version";

// Tier-7 §3.I test seam. If the URL carries `?menese_test_seed=<hex32>`
// we install a deterministic Ed25519 identity factory on window before
// the live II flow ever runs. Production pages without the query param
// are unaffected. The factory is consumed by `IiAuth.maybeTestIdentity`.
//
// Additionally, when test mode is active, we expose a `__MENESE_TEST_API__`
// surface that the §3.I oracle-verified browser tests use to inspect
// internal state (unwrapped x_user, wasm parity output, etc). The
// surface is gated on the seed query param — the page never exposes
// it in normal use.
function maybeInstallTestIdentity(): void {
  const params = new URLSearchParams(window.location.search);
  const seedHex = params.get("menese_test_seed");
  if (!seedHex || !/^[0-9a-fA-F]{64}$/.test(seedHex)) return;
  const seed = new Uint8Array(seedHex.match(/.{2}/g)!.map((b) => parseInt(b, 16)));
  window.__MENESE_TEST_IDENTITY__ = () => Ed25519KeyIdentity.generate(seed);

  // Expose limited helpers to tests. Imports happen lazily — the user's
  // x_user is NEVER exposed unless the page is in test mode.
  void (async () => {
    const passkey = await import("./lib/passkey");
    const idl = await import("./lib/canister/idl");
    const flow = await import("./lib/flow");
    const dfinityAgent = await import("@dfinity/agent");
    (window as unknown as { __MENESE_TEST_API__: unknown }).__MENESE_TEST_API__ = {
      passkey,
      idl,
      flow,
      Ed25519KeyIdentity,
      Actor: dfinityAgent.Actor,
      HttpAgent: dfinityAgent.HttpAgent,
    };
  })();
}
maybeInstallTestIdentity();

const CANISTER_ID =
  (import.meta as { env?: { VITE_CANISTER_ID?: string } }).env
    ?.VITE_CANISTER_ID ?? "ufxgi-4p777-77774-qaadq-cai";
// Replica host: explicit `VITE_REPLICA_URL` wins, else use the IC
// mainnet API on production builds and the local dfx replica on dev
// builds. `import.meta.env.PROD` is set by vite at build time
// (true for `pnpm build`, false for `pnpm dev`).
const PROD_BUILD =
  (import.meta as { env?: { PROD?: boolean } }).env?.PROD === true;
const REPLICA_URL =
  (import.meta as { env?: { VITE_REPLICA_URL?: string } }).env
    ?.VITE_REPLICA_URL ??
  (PROD_BUILD ? "https://icp-api.io" : "http://127.0.0.1:4945");

interface AppState {
  iiAuth: IiAuth;
  canister?: MeneseCanister;
  principal?: string;
  combinedPubkeyHex?: string;
}

const state: AppState = {
  iiAuth: new IiAuth(),
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Partial<HTMLElementTagNameMap[K]>,
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (props) Object.assign(e, props);
  for (const c of children) {
    e.append(c);
  }
  return e;
}

function escapeHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function renderLoggedOut(): HTMLElement {
  const button = el("button", { textContent: "Log in with Internet Identity" });
  button.addEventListener("click", () => {
    void onLogin();
  });
  return el("div", {}, [
    el("h1", { textContent: "Menese 2-of-2 — NIM Threshold ECDSA" }),
    el("p", {
      className: "subtitle",
      textContent:
        "Sign on the Internet Computer with a passkey-bound user share. The canister never holds your secret.",
    }),
    el("div", { className: "card" }, [button]),
    el("p", {
      className: "label",
      textContent: `Combined version v${MENESE_COMBINED_VERSION} · canister ${CANISTER_ID}`,
    }),
  ]);
}

function renderSignedIn(): HTMLElement {
  const messageInput = el("textarea", {
    placeholder: "Enter a message to sign…",
  });
  const signButton = el("button", { textContent: "Sign" });
  const result = el("div", { className: "card" });

  signButton.addEventListener("click", () => {
    void onSign(messageInput.value, result);
  });

  const logout = el("button", { textContent: "Log out" });
  logout.addEventListener("click", () => {
    void onLogout();
  });

  return el("div", {}, [
    el("h1", { textContent: "Menese 2-of-2" }),
    el("p", {
      className: "subtitle",
      textContent: `Signed in as ${state.principal ?? "(unknown)"}`,
    }),
    el("div", { className: "card" }, [
      el("span", {
        className: "label",
        textContent: "Combined ECDSA pubkey (SEC1 hex)",
      }),
      el("div", {
        className: "mono",
        textContent: state.combinedPubkeyHex ?? "(unknown)",
      }),
    ]),
    el("h2", { textContent: "Sign a message" }),
    el("div", { className: "card" }, [messageInput, el("br"), signButton, result]),
    el("h2", { textContent: "Session" }),
    el("div", { className: "card" }, [logout]),
  ]);
}

async function onLogin(): Promise<void> {
  setError(null);
  try {
    const session = await state.iiAuth.login();
    await onAuthenticated(session.principal.toText(), session.identity);
  } catch (err) {
    setError(err);
  }
}

async function onAuthenticated(
  principalText: string,
  identity: import("@dfinity/agent").Identity,
): Promise<void> {
  state.principal = principalText;
  state.canister = new MeneseCanister({
    canisterId: CANISTER_ID,
    identity,
    replicaUrl: REPLICA_URL,
  });
  await assertCombinedVersionMatches(state.canister, MENESE_COMBINED_VERSION);

  // If the user is registered already, fetch combined_pk; else run
  // onboarding (passkey register + AEAD wrap + canister register).
  const cpk = await state.canister.combinedPublicKey();
  if ("Ok" in cpk) {
    state.combinedPubkeyHex = escapeHex(cpk.Ok);
    render(renderSignedIn());
    // Critical-path warm: kick off background presig prefetch so the
    // first sign click is one IC update call. Fire-and-forget — sign
    // falls back to synchronous fetch on cache miss.
    void prefetchPresig(state.canister);
    return;
  }

  // Onboarding required.
  if (!hasLocalCredentialId()) {
    render(
      el("div", {}, [
        el("h1", { textContent: "Setting up your passkey…" }),
        el("p", {
          className: "subtitle",
          textContent:
            "Authorize a passkey to bind your wallet to this device. Cross-device works automatically when your passkey syncs (Apple Keychain / Google / 1Password).",
        }),
      ]),
    );
  }
  const result = await onboardingRegister({
    canister: state.canister,
    iiPrincipal: (await state.iiAuth.getCurrentSession())!.principal,
  });
  state.combinedPubkeyHex = escapeHex(result.combinedPubkey);
  render(renderSignedIn());
  void prefetchPresig(state.canister);
}

async function onSign(message: string, target: HTMLElement): Promise<void> {
  if (!state.canister) return;
  target.textContent = "Signing…";
  try {
    const sig = await signMessage({
      canister: state.canister,
      message: new TextEncoder().encode(message),
    });
    target.replaceChildren(
      el("span", { className: "label", textContent: "Message hash (SHA-256)" }),
      el("div", { className: "mono", textContent: escapeHex(sig.messageHash) }),
      el("span", { className: "label", textContent: "Presig ID" }),
      el("div", { className: "mono", textContent: escapeHex(sig.presigId) }),
      el("span", { className: "label", textContent: "Signature r" }),
      el("div", { className: "mono", textContent: escapeHex(sig.r) }),
      el("span", { className: "label", textContent: "Signature s" }),
      el("div", { className: "mono", textContent: escapeHex(sig.s) }),
      el("p", { className: "success", textContent: "✓ k256 verify_prehash OK" }),
    );
  } catch (err) {
    target.replaceChildren(
      el("p", { className: "error", textContent: String(err) }),
    );
  }
}

async function onLogout(): Promise<void> {
  const session = await state.iiAuth.getCurrentSession();
  if (session) await session.logout();
  clearSessionCache();
  state.canister = undefined;
  state.principal = undefined;
  state.combinedPubkeyHex = undefined;
  render(renderLoggedOut());
}

function render(node: HTMLElement): void {
  const root = document.getElementById("app");
  if (!root) throw new Error("app root missing");
  root.replaceChildren(node);
}

function setError(err: unknown): void {
  const root = document.getElementById("app");
  if (!root) return;
  if (err === null) return;
  root.append(
    el("p", { className: "error", textContent: String(err) }),
  );
}

async function main(): Promise<void> {
  try {
    const session = await state.iiAuth.getCurrentSession();
    if (session) {
      await onAuthenticated(session.principal.toText(), session.identity);
    } else {
      render(renderLoggedOut());
    }
  } catch (err) {
    console.error("[menese] main() failed:", err);
    const root = document.getElementById("app");
    if (root) {
      root.replaceChildren(
        el("h1", { textContent: "Menese 2-of-2 — error" }),
        el("p", { className: "error", textContent: String(err) }),
      );
    }
  }
}

void main();
